import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { REPORT_METRICS, GROUP_BY_OPTIONS } from '../../config/reportMetrics';
import { FilterBuilderModal } from './FilterBuilderModal';
import { ReportVisualization } from './ReportVisualization';
import { ReportExportMenu } from './ReportExportMenu';
import { aggregateReportData, aggregateWithComparison, applyFilters } from '../../utils/reportAggregation';
import {
    fetchSavedReportConfigs,
    createSavedReportConfig,
    updateSavedReportConfig,
    fetchExpenseRecords,
    fetchPMReports,
    fetchTireReports,
    fetchUtilizationReportRecords,
} from '../../graphql/reportQueries';
import { isUtilizationReportMetric } from '../../utils/utilizationReportMetrics';
import { useUserContext } from '../../contexts/UserContext';
import { BackButton } from '../ui';
import type {
    FilterCondition,
    ReportConfiguration,
    AggregatedDataPoint,
    MetricDefinition,
    MetricCategory,
} from '../../types/reportTypes';
import '../../css/CustomReportBuilder.css';

interface CustomReportBuilderProps {
    companyId: string;
    userId: string;
    configId?: string;
    /** Fixed display title shown in the header (won't change when input is edited) */
    displayTitle?: string;
    /** When set, shows a back button that navigates to /reports and activates this tab */
    backTab?: string;
    /** Pre-populated defaults from a standard report catalog entry */
    defaults?: {
        reportName?: string;
        metricKey?: string;
        groupByField?: string;
        visualizationType?: 'table' | 'bar' | 'pie' | 'line';
    };
}

const METRIC_CATEGORY_LABELS: Record<MetricCategory, string> = {
    'cost-financial': 'Cost & Financial',
    'fuel-usage': 'Fuel & Usage',
    'pm-maintenance': 'PM & Maintenance',
    'tire-metrics': 'Tire Metrics',
    'utilization-performance': 'Utilization & Performance',
    safety: 'Safety',
};

const METRIC_CATEGORY_ORDER: MetricCategory[] = [
    'cost-financial',
    'fuel-usage',
    'pm-maintenance',
    'tire-metrics',
    'utilization-performance',
    'safety',
];

const VIZ_OPTIONS: { key: ReportConfiguration['visualizationType']; label: string }[] = [
    { key: 'table', label: 'Table' },
    { key: 'bar', label: 'Bar' },
    { key: 'pie', label: 'Pie' },
    { key: 'line', label: 'Line' },
];

/**
 * Custom Report Builder — configuration-driven report creation UI.
 * Supports metric selection, date ranges, grouping, dynamic filters,
 * and four visualization types. Can load saved configs or catalog defaults.
 *
 * Validates: Requirements 6.1–6.7, 8.1–8.7, 9.1, 9.2, 10.1
 */
export const CustomReportBuilder = ({
    companyId,
    userId,
    configId: configIdProp,
    displayTitle,
    backTab,
    defaults,
}: CustomReportBuilderProps) => {
    const { company } = useUserContext();
    const { configId: configIdParam } = useParams<{ configId?: string }>();
    const configId = configIdProp || configIdParam;
    const navigate = useNavigate();
    const vizRef = useRef<HTMLDivElement>(null);

    const handleBack = useCallback(() => {
        if (backTab) {
            try {
                localStorage.setItem('reportsActiveTab', backTab);
            } catch {
                /* ignore */
            }
        }
        navigate('/reports');
    }, [backTab, navigate]);

    // Config state
    const [reportName, setReportName] = useState(defaults?.reportName || '');
    const [metricKey, setMetricKey] = useState(defaults?.metricKey || '');
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [compareEnabled, setCompareEnabled] = useState(false);
    const [compareDateStart, setCompareDateStart] = useState('');
    const [compareDateEnd, setCompareDateEnd] = useState('');
    const [groupByField, setGroupByField] = useState(defaults?.groupByField || 'none');
    const [timeSubOption, setTimeSubOption] = useState('monthly');
    const [filters, setFilters] = useState<FilterCondition[]>([]);
    const [vizType, setVizType] = useState<ReportConfiguration['visualizationType']>(
        defaults?.visualizationType || 'table',
    );

    // Data state
    const [, setRawData] = useState<Record<string, any>[]>([]);
    const [aggregatedData, setAggregatedData] = useState<AggregatedDataPoint[]>([]);
    const [comparisonData, setComparisonData] = useState<AggregatedDataPoint[] | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

    // Save dropdown
    const [showSaveDropdown, setShowSaveDropdown] = useState(false);
    const [savedConfigId, setSavedConfigId] = useState<string | undefined>(configId);
    const saveRef = useRef<HTMLDivElement>(null);

    // Close save dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (saveRef.current && !saveRef.current.contains(e.target as Node)) {
                setShowSaveDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Module enrollment gating
    const enrollmentFlags = useMemo(
        () => ({
            pmModuleEnrollment: !!(company as any)?.pmModuleEnrollment,
            tiresModuleEnrollment: !!(company as any)?.tiresModuleEnrollment,
            trainingModuleEnrollment: !!(company as any)?.trainingModuleEnrollment,
        }),
        [company],
    );

    // Filter metrics by module enrollment
    const availableMetrics = useMemo(() => {
        return REPORT_METRICS.filter((m) => {
            if (!m.requiredModuleEnrollment) return true;
            return !!(enrollmentFlags as any)[m.requiredModuleEnrollment];
        });
    }, [enrollmentFlags]);

    // Group metrics by category
    const metricsByCategory = useMemo(() => {
        const map = new Map<MetricCategory, MetricDefinition[]>();
        for (const m of availableMetrics) {
            if (!map.has(m.category)) map.set(m.category, []);
            map.get(m.category)!.push(m);
        }
        return map;
    }, [availableMetrics]);

    const selectedMetric = useMemo(() => REPORT_METRICS.find((m) => m.key === metricKey), [metricKey]);

    const selectedGroupBy = GROUP_BY_OPTIONS.find((g) => g.key === groupByField);

    // Load saved config if configId provided
    useEffect(() => {
        if (!configId || !companyId) return;
        fetchSavedReportConfigs(companyId)
            .then((configs) => {
                const config = configs.find((c: any) => c.id === configId);
                if (config) {
                    setReportName(config.reportName || '');
                    setMetricKey(config.metricKey || '');
                    setGroupByField(config.groupByField || 'none');
                    setVizType((config.visualizationType as 'table' | 'line' | 'bar' | 'pie') || 'table');
                    if (config.dateRangeStart) setDateStart(config.dateRangeStart.split('T')[0]);
                    if (config.dateRangeEnd) setDateEnd(config.dateRangeEnd.split('T')[0]);
                    if (config.compareDateRangeStart) {
                        setCompareEnabled(true);
                        setCompareDateStart(config.compareDateRangeStart.split('T')[0]);
                    }
                    if (config.compareDateRangeEnd) {
                        setCompareDateEnd(config.compareDateRangeEnd.split('T')[0]);
                    }
                    if (config.filtersJson) {
                        try {
                            const parsed = JSON.parse(config.filtersJson as string);
                            if (Array.isArray(parsed)) setFilters(parsed);
                        } catch {
                            /* ignore */
                        }
                    }
                    setSavedConfigId(config.id);
                }
            })
            .catch((err) => {
                console.error('Failed to load saved config:', err);
                Sentry.captureException(err);
            });
    }, [configId, companyId]);

    // Build report configuration
    const reportConfig = useMemo<ReportConfiguration>(
        () => ({
            metricKey,
            dateRangeStart: dateStart,
            dateRangeEnd: dateEnd,
            compareDateRangeStart: compareEnabled ? compareDateStart : undefined,
            compareDateRangeEnd: compareEnabled ? compareDateEnd : undefined,
            groupByField: groupByField === 'date' ? `date-${timeSubOption}` : groupByField,
            filters,
            visualizationType: vizType,
        }),
        [
            metricKey,
            dateStart,
            dateEnd,
            compareEnabled,
            compareDateStart,
            compareDateEnd,
            groupByField,
            timeSubOption,
            filters,
            vizType,
        ],
    );

    // Fetch and aggregate data when config changes
    const fetchAndAggregate = useCallback(async () => {
        if (!metricKey || !companyId) return;
        setIsLoading(true);

        try {
            const metric = REPORT_METRICS.find((m) => m.key === metricKey);
            if (!metric) return;

            // Fetch raw data based on metric's data source
            let records: any[] = [];
            switch (metric.dataSource) {
                case 'ExpenseRecord':
                    records = await fetchExpenseRecords(companyId);
                    break;
                case 'PMReport':
                    records = await fetchPMReports(companyId);
                    break;
                case 'Tire':
                    records = await fetchTireReports(companyId);
                    break;
                case 'SingleUnit':
                    if (isUtilizationReportMetric(metric.key)) {
                        const derived = await fetchUtilizationReportRecords(companyId);
                        // Incomplete ITD inputs leave the percent null. Drop those rows so
                        // the average does not turn "no pace" into 0%.
                        records = derived.filter((record) => {
                            const value = record[metric.field];
                            return typeof value === 'number' && Number.isFinite(value);
                        });
                    } else {
                        records = await fetchExpenseRecords(companyId);
                    }
                    break;
                default:
                    records = await fetchExpenseRecords(companyId);
                    break;
            }

            setRawData(records);

            // Apply filters
            const filtered = applyFilters(records, filters);

            // Aggregate
            const groupField = groupByField === 'date' ? `date-${timeSubOption}` : groupByField;

            if (compareEnabled && compareDateStart && compareDateEnd) {
                const result = aggregateWithComparison(filtered, reportConfig, metric);
                setAggregatedData(result.primary);
                setComparisonData(result.comparison);
            } else {
                const result = aggregateReportData(filtered, {
                    metricField: metric.field,
                    groupByField: groupField,
                    aggregation: metric.aggregation,
                    dateRangeStart: dateStart,
                    dateRangeEnd: dateEnd,
                });
                setAggregatedData(result);
                setComparisonData(undefined);
            }
        } catch (err) {
            console.error('Failed to fetch report data:', err);
            Sentry.captureException(err);
            setAggregatedData([]);
            setComparisonData(undefined);
        } finally {
            setIsLoading(false);
        }
    }, [
        metricKey,
        companyId,
        filters,
        groupByField,
        timeSubOption,
        dateStart,
        dateEnd,
        compareEnabled,
        compareDateStart,
        compareDateEnd,
        reportConfig,
    ]);

    useEffect(() => {
        fetchAndAggregate();
    }, [fetchAndAggregate]);

    // Filter handlers
    const handleAddFilter = (condition: FilterCondition) => {
        setFilters((prev) => [...prev, condition]);
    };

    const handleRemoveFilter = (index: number) => {
        setFilters((prev) => prev.filter((_, i) => i !== index));
    };

    // Save handlers
    const handleSave = async () => {
        const input: any = {
            companyId,
            userId,
            reportName: reportName || 'Untitled Report',
            metricKey,
            metricCategory: selectedMetric?.category || '',
            dateRangeStart: dateStart ? new Date(dateStart).toISOString() : null,
            dateRangeEnd: dateEnd ? new Date(dateEnd).toISOString() : null,
            compareDateRangeStart: compareEnabled && compareDateStart ? new Date(compareDateStart).toISOString() : null,
            compareDateRangeEnd: compareEnabled && compareDateEnd ? new Date(compareDateEnd).toISOString() : null,
            groupByField: groupByField === 'date' ? `date-${timeSubOption}` : groupByField,
            filtersJson: JSON.stringify(filters),
            visualizationType: vizType,
            lastRunAt: new Date().toISOString(),
        };

        try {
            if (savedConfigId) {
                await updateSavedReportConfig({ id: savedConfigId, ...input });
            } else {
                const result = await createSavedReportConfig(input);
                if (result) setSavedConfigId(result.id);
            }
        } catch (err) {
            console.error('Failed to save report config:', err);
            Sentry.captureException(err);
        }
        setShowSaveDropdown(false);
    };

    const handleSaveAsNew = async () => {
        const input: any = {
            companyId,
            userId,
            reportName: `${reportName || 'Untitled Report'} (Copy)`,
            metricKey,
            metricCategory: selectedMetric?.category || '',
            dateRangeStart: dateStart ? new Date(dateStart).toISOString() : null,
            dateRangeEnd: dateEnd ? new Date(dateEnd).toISOString() : null,
            compareDateRangeStart: compareEnabled && compareDateStart ? new Date(compareDateStart).toISOString() : null,
            compareDateRangeEnd: compareEnabled && compareDateEnd ? new Date(compareDateEnd).toISOString() : null,
            groupByField: groupByField === 'date' ? `date-${timeSubOption}` : groupByField,
            filtersJson: JSON.stringify(filters),
            visualizationType: vizType,
            lastRunAt: new Date().toISOString(),
        };

        try {
            const result = await createSavedReportConfig(input);
            if (result) setSavedConfigId(result.id);
            setReportName(input.reportName);
        } catch (err) {
            console.error('Failed to save report config as new:', err);
            Sentry.captureException(err);
        }
        setShowSaveDropdown(false);
    };

    const formatFilterChip = (f: FilterCondition) => {
        const valStr = Array.isArray(f.value) ? f.value.join(', ') : String(f.value);
        return `${f.fieldLabel} ${f.operator} ${valStr}`;
    };

    return (
        <div className="page-container custom-report-builder">
            {backTab && (
                <BackButton
                    onClick={handleBack}
                    buttonText="Back to Reports & Analytics"
                    ariaLabel="Back to Reports & Analytics"
                />
            )}
            <div className="crb-header">
                <h2>{displayTitle || reportName || 'Custom Report'}</h2>
                <div className="crb-header-actions">
                    <ReportExportMenu
                        data={aggregatedData}
                        config={reportConfig}
                        reportName={reportName}
                        targetRef={vizRef}
                    />
                    <div className="crb-save-wrapper" ref={saveRef}>
                        <button className="crb-save-btn" onClick={handleSave} disabled={!reportName.trim()}>
                            Save Report
                        </button>
                        <button
                            className="crb-save-dropdown-toggle"
                            onClick={() => setShowSaveDropdown((p) => !p)}
                            aria-label="Save options"
                            disabled={!reportName.trim()}
                        >
                            ▼
                        </button>
                        {showSaveDropdown && (
                            <div className="crb-save-dropdown">
                                <button onClick={handleSave}>Save</button>
                                <button onClick={handleSaveAsNew}>Save as new custom report</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="crb-config">
                {/* Report Name */}
                <div className="crb-field">
                    <label htmlFor="crb-name">Report Name</label>
                    <input
                        id="crb-name"
                        type="text"
                        value={reportName}
                        onChange={(e) => setReportName(e.target.value)}
                        placeholder="Enter report name"
                    />
                </div>

                {/* Metric */}
                <div className="crb-field">
                    <label htmlFor="crb-metric">Metric</label>
                    <select
                        id="crb-metric"
                        className="crb-metric-select"
                        value={metricKey}
                        onChange={(e) => setMetricKey(e.target.value)}
                    >
                        <option value="">Select a metric...</option>
                        {METRIC_CATEGORY_ORDER.map((cat) => {
                            const metrics = metricsByCategory.get(cat);
                            if (!metrics || metrics.length === 0) return null;
                            return (
                                <optgroup key={cat} label={METRIC_CATEGORY_LABELS[cat]}>
                                    {metrics.map((m) => (
                                        <option key={m.key} value={m.key}>
                                            {m.label}
                                        </option>
                                    ))}
                                </optgroup>
                            );
                        })}
                    </select>
                </div>

                {/* Duration */}
                <div className="crb-field">
                    <label>Duration</label>
                    <div className="crb-date-row">
                        <input
                            type="date"
                            value={dateStart}
                            onChange={(e) => setDateStart(e.target.value)}
                            aria-label="Start date"
                        />
                        <span>to</span>
                        <input
                            type="date"
                            value={dateEnd}
                            onChange={(e) => setDateEnd(e.target.value)}
                            aria-label="End date"
                        />
                    </div>
                    <label className="crb-compare-check">
                        <input
                            type="checkbox"
                            checked={compareEnabled}
                            onChange={(e) => setCompareEnabled(e.target.checked)}
                        />
                        Compare To Another Range
                    </label>
                    {compareEnabled && (
                        <div className="crb-date-row" style={{ marginTop: 6 }}>
                            <input
                                type="date"
                                value={compareDateStart}
                                onChange={(e) => setCompareDateStart(e.target.value)}
                                aria-label="Comparison start date"
                            />
                            <span>to</span>
                            <input
                                type="date"
                                value={compareDateEnd}
                                onChange={(e) => setCompareDateEnd(e.target.value)}
                                aria-label="Comparison end date"
                            />
                        </div>
                    )}
                </div>

                {/* Group By */}
                <div className="crb-field">
                    <label>Group By</label>
                    <div className="crb-group-by-row">
                        <select
                            value={groupByField}
                            onChange={(e) => setGroupByField(e.target.value)}
                            aria-label="Group by"
                        >
                            {GROUP_BY_OPTIONS.map((opt) => (
                                <option key={opt.key} value={opt.key}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        {selectedGroupBy?.timeSubOptions && (
                            <select
                                value={timeSubOption}
                                onChange={(e) => setTimeSubOption(e.target.value)}
                                aria-label="Time grouping"
                            >
                                {selectedGroupBy.timeSubOptions.map((sub) => (
                                    <option key={sub.key} value={sub.key}>
                                        {sub.label}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="crb-filters-row">
                {filters.map((f, i) => (
                    <span key={i} className="crb-filter-chip">
                        {formatFilterChip(f)}
                        <button
                            className="crb-filter-chip-remove"
                            onClick={() => handleRemoveFilter(i)}
                            aria-label={`Remove filter ${f.fieldLabel}`}
                        >
                            ×
                        </button>
                    </span>
                ))}
                <button className="crb-add-filter-btn" onClick={() => setIsFilterModalOpen(true)}>
                    + Filters
                </button>
            </div>

            {/* View Toggle */}
            <div className="crb-view-toggle" role="group" aria-label="Visualization">
                {VIZ_OPTIONS.map((opt) => (
                    <button
                        key={opt.key}
                        type="button"
                        className={`crb-view-btn ${vizType === opt.key ? 'active' : ''}`}
                        aria-pressed={vizType === opt.key}
                        onClick={() => setVizType(opt.key)}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            {/* Visualization */}
            <div className="crb-visualization" ref={vizRef}>
                <ReportVisualization
                    data={aggregatedData}
                    visualizationType={vizType}
                    comparisonData={comparisonData}
                    isLoading={isLoading}
                    metricLabel={selectedMetric?.label || 'Value'}
                    groupByLabel={selectedGroupBy?.label || 'Group'}
                />
            </div>

            {/* Filter Modal */}
            <FilterBuilderModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                onApply={handleAddFilter}
                selectedMetricKey={metricKey}
                companyId={companyId}
            />
        </div>
    );
};
